import type { FC, ReactNode } from 'react';
import { FileText, CheckCircle2, Clock3, CalendarDays, TrendingUp } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Loader } from '@/components/ui';
import { useHistoryStats } from '@/lib/api/hooks/history';

interface StatsCardProps {
	title: string;
	value: string | number;
	description: ReactNode;
	icon: ReactNode;
	iconContainerClassName: string;
	iconClassName: string;
	trend?: ReactNode;
}



interface StatConfig {
	key: string;
	title: string;
	icon: LucideIcon;
	iconContainerClassName: string;
	iconClassName: string;
}

const StatCard = ({
	title,
	value,
	description,
	icon,
	iconContainerClassName,
	iconClassName,
	trend,
}: StatsCardProps) => {
	return (
		<div className="rounded-xl border 2xl:w-fit border-gray-200 bg-white px-6 py-5 shadow-sm">
			<div className="grid grid-cols-[auto_1fr] gap-4">
				<div className={`flex h-12 w-12 xl:w-14 xl:h-14 items-center justify-center rounded-md ${iconContainerClassName}`}>
					<span className={iconClassName}>{icon}</span>
				</div>
				<div className="flex items-center gap-4 justify-between flex-wrap">
					<div>

					<p className="text-base font-medium text-gray-600">{title}</p>
             <p className={`leading-[1.1] font-semibold text-gray-900 ${title.toLowerCase() === 'last updated' ? 'text-[1.25rem]' : 'text-[2rem]'}`}>{value}</p>
					<div className="text-sm text-gray-500">{description}</div>
					</div>
				{trend}
				</div>
			</div>
		</div>
	);
};

const formatChangeValue = (value: number): string => {
	const absValue = Math.abs(value);
	const formatted = Number.isInteger(absValue)
		? absValue.toString()
		: absValue.toFixed(1).replace(/\.0$/, '');

	return `${formatted}%`;
};

const formatDate = (dateString?: string | null) => {
	if (!dateString) return '-';
	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) return '-';
	return date.toLocaleDateString('en-GB', {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	});
};

const getTrendBadge = (value: number) => {
	const isPositive = value >= 0;
	return (
		<div className={`inline-flex items-center h-fit my-auto gap-1 rounded px-2 py-1 text-sm font-semibold ${isPositive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
			<TrendingUp className={`w-3.5 h-3.5 ${!isPositive ? 'rotate-180' : ''}`} />
			{formatChangeValue(value)}
		</div>
	);
};

const STATS_CONFIG: StatConfig[] = [
	{
		key: 'total',
		title: 'Total Histories',
		icon: FileText,
		iconContainerClassName: 'bg-emerald-50',
		iconClassName: 'text-emerald-700',
	},
	{
		key: 'totalPublished',
		title: 'Published',
		icon: CheckCircle2,
		iconContainerClassName: 'bg-emerald-50',
		iconClassName: 'text-emerald-700',
	},
	{
		key: 'totalDrafts',
		title: 'Drafts',
		icon: Clock3,
		iconContainerClassName: 'bg-amber-50',
		iconClassName: 'text-amber-600',
	},
	{
		key: 'latestUpdatedAt',
		title: 'Last Updated',
		icon: CalendarDays,
		iconContainerClassName: 'bg-violet-50',
		iconClassName: 'text-violet-700',
	},
];

const HistoryStats: FC = () => {
	const { data: stats, isFetching, refetch, isError } = useHistoryStats();

	const updatedByName = (stats as unknown as { updatedByName?: string })?.updatedByName;

	const values = {
		total: stats?.totalHistories?.totalCount ?? 0,
		totalPublished: stats?.publishedHistories?.totalCount ?? 0,
		totalDrafts: stats?.draftHistories?.totalCount ?? 0,
		latestUpdatedAt: formatDate(stats?.latestUpdatedAt),
	};

 const getDescription = (key: string) => {
	switch (key) {
		case 'total':
			return 'Across all';
		case 'totalPublished':
			return 'Live on website';
		case 'totalDrafts':
			return 'Not yet published';
		case 'latestUpdatedAt':
			return updatedByName ? `By ${updatedByName}` : '';
		default:
			return '';
	}
};

	const handleTrendBadge = (key: string) => {
		switch (key) {
			case 'total':
				return getTrendBadge(stats?.totalHistories?.percentageChange ?? 0);
			case 'totalPublished':
				return getTrendBadge(stats?.publishedHistories?.percentageChange ?? 0);
			case 'totalDrafts':
				return getTrendBadge(stats?.draftHistories?.percentageChange ?? 0);
			default:
				return null;
		}
	};


	return (
		<section className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:flex 2xl:min-w-[75%]">
			{isError ? (
				<div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4 sm:col-span-2 2xl:col-span-4">
					<p>Error loading statistics.</p>
					<button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded">Retry</button>
				</div>
			) : isFetching ? (
				STATS_CONFIG.map((stat) => {
					return (
						<div key={stat.key} className="p-4 flex items-center justify-center h-full min-h-28 w-full">
							<Loader size="md" variant="wine" />
						</div>
					);
				})
			) : (
				STATS_CONFIG.map((stat) => {
					const Icon = stat.icon;

								return (
							<StatCard
								key={stat.key}
								title={stat.title}
								value={values[stat.key as keyof typeof values]}
								description={getDescription(stat.key)}
								icon={<Icon className="h-5 w-5 xl:w-8 xl:h-8" strokeWidth={1.5} />}
								iconContainerClassName={stat.iconContainerClassName}
								iconClassName={stat.iconClassName}
								trend={handleTrendBadge(stat.key)}
							/>
						);

					
				})
			)}
		</section>
	);
};

export default HistoryStats;
